/**
 * PagingRenderer - Renderer for pagination settings
 *
 * Allows enabling pagination and setting maximum items to retrieve.
 */

import { makeStyles, tokens, Text, Switch, SpinButton, Field } from '@coreai/fluentui-react';
import type { SettingRendererProps } from './registry';
import { registerSettingRenderer } from './registry';
import type { PagingSettings } from '@/types/nodeSettings';

import {
  settingContainerStyle,
  settingDescriptionStyle,
  settingHeaderStyle,
  settingLabelContainerStyle,
  settingLabelStyle,
} from './settingRendererStyles';

const useStyles = makeStyles({
  container: settingContainerStyle,
  header: settingHeaderStyle,
  labelContainer: settingLabelContainerStyle,
  label: settingLabelStyle,
  description: settingDescriptionStyle,
  inputContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  spinButton: {
    width: '120px',
  },
});

export interface PagingRendererProps extends SettingRendererProps<PagingSettings> {
  label?: string;
  description?: string;
}

/**
 * PagingRenderer component for pagination configuration
 */
export const PagingRenderer = ({
  setting,
  onChange,
  readOnly = false,
  label = 'Pagination',
  description = 'Retrieve multiple pages of results',
}: PagingRendererProps) => {
  const styles = useStyles();

  if (!setting.isSupported) {
    return null;
  }

  const currentValue = setting.value ?? { enabled: false };

  const handleToggle = (_: React.ChangeEvent<HTMLInputElement>, data: { checked: boolean }) => {
    onChange({
      ...currentValue,
      enabled: data.checked,
    });
  };

  const handleCountChange = (_: React.SyntheticEvent, data: { value?: number | null }) => {
    if (data.value !== undefined && data.value !== null) {
      onChange({
        ...currentValue,
        value: data.value,
      });
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.labelContainer}>
          <Text className={styles.label}>{label}</Text>
          <Text className={styles.description}>{description}</Text>
        </div>
        <Switch
          checked={currentValue.enabled}
          onChange={handleToggle}
          disabled={readOnly}
          aria-label={`Enable ${label}`}
        />
      </div>

      {currentValue.enabled && (
        <div className={styles.inputContainer}>
          <Field label="Maximum items">
            <SpinButton
              className={styles.spinButton}
              value={currentValue.value ?? 100}
              onChange={handleCountChange}
              min={1}
              max={100000}
              step={100}
              disabled={readOnly}
              aria-label="Maximum items to retrieve"
            />
          </Field>
        </div>
      )}
    </div>
  );
};

PagingRenderer.displayName = 'PagingRenderer';

registerSettingRenderer('paging', PagingRenderer as React.ComponentType<SettingRendererProps<unknown>>);
